import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaSignInAlt,
  FaHeadset,
  FaBullhorn,
} from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { useAppContext } from "../context/AppContext";
import AnnouncementPopupModal from "./AnnouncementPopupModal";
import WithdrawTicker from "./WithdrawTicker";
import ThreeWorkersLogo from "./logo";

/* ─── localStorage helpers (logic unchanged) ─── */
const SEEN_KEY = "seen_announcement";
const SEEN_TTL_MS = 10 * 60 * 1000;

const getSeen = () => {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (Date.now() > Number(obj.expiresAt)) {
      localStorage.removeItem(SEEN_KEY);
      return null;
    }
    return obj;
  } catch {
    localStorage.removeItem(SEEN_KEY);
    return null;
  }
};

const setSeen = (id) => {
  try {
    localStorage.setItem(
      SEEN_KEY,
      JSON.stringify({ id: String(id), expiresAt: Date.now() + SEEN_TTL_MS })
    );
  } catch {}
};

/* ─── Theme tokens ─── */
const T = {
  bg: "#050d1a",
  bgCard: "#0a1628",
  border: "#1a2d4a",
  accent: "#eab308",
  accentDim: "rgba(234,179,8,0.15)",
  accentGlow: "rgba(234,179,8,0.35)",
  blue: "#3b82f6",
  blueDim: "rgba(59,130,246,0.15)",
  textPrimary: "#f0f6ff",
  textSecondary: "#94a3b8",
  textMuted: "#4b6280",
  danger: "#ef4444",
  dangerDim: "rgba(239,68,68,0.12)",
};

/* ─── Animated 3-axis grid canvas for navbar bg ─── */
function NavCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let t = 0;

    const W = canvas.offsetWidth || 1440;
    const H = canvas.offsetHeight || 80;
    canvas.width = W * devicePixelRatio;
    canvas.height = H * devicePixelRatio;
    ctx.scale(devicePixelRatio, devicePixelRatio);

    const COLS = 28;
    const ROWS = 5;
    const cellW = W / COLS;
    const cellH = H / ROWS;

    function loop() {
      ctx.clearRect(0, 0, W, H);
      t += 0.012;

      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          const wave =
            Math.sin((c / COLS) * Math.PI * 2 + t) * 3 +
            Math.cos((r / ROWS) * Math.PI * 1.5 + t * 0.7) * 2;

          const x = c * cellW;
          const y = r * cellH + wave;
          const intensity = Math.abs(wave) / 5;

          /* horizontal grid lines */
          ctx.beginPath();
          ctx.moveTo(x, y + cellH);
          ctx.lineTo(x + cellW, y + cellH);
          ctx.strokeStyle = `rgba(234,179,8,${(0.025 + intensity * 0.04).toFixed(3)})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();

          /* vertical grid lines */
          ctx.beginPath();
          ctx.moveTo(x + cellW, y);
          ctx.lineTo(x + cellW, y + cellH);
          ctx.strokeStyle = `rgba(59,130,246,${(0.018 + intensity * 0.025).toFixed(3)})`;
          ctx.lineWidth = 0.4;
          ctx.stroke();

          /* glow dots at intersections */
          if ((c + r) % 5 === 0) {
            const gAlpha = 0.04 + intensity * 0.12;
            ctx.beginPath();
            ctx.arc(x + cellW, y + cellH, 1.2, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(234,179,8,${gAlpha.toFixed(3)})`;
            ctx.fill();
          }
        }
      }
      animId = requestAnimationFrame(loop);
    }
    loop();
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        opacity: 0.9,
      }}
    />
  );
}

/* ─── Icon button (dark theme) ─── */
const NavIconButton = ({ icon, onClick, className = "" }) => (
  <button
    onClick={onClick}
    style={{
      width: 40,
      height: 40,
      borderRadius: 12,
      background: "rgba(255,255,255,0.04)",
      border: `1px solid ${T.border}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: T.textSecondary,
      cursor: "pointer",
      transition: "all 0.2s",
      fontSize: 14,
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = T.accent;
      e.currentTarget.style.color = T.accent;
      e.currentTarget.style.background = T.accentDim;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = T.border;
      e.currentTarget.style.color = T.textSecondary;
      e.currentTarget.style.background = "rgba(255,255,255,0.04)";
    }}
    className={className}
  >
    {icon}
  </button>
);

/* ─── NavLink ─── */
const NavLink = ({ to, children, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={to}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        fontSize: 10,
        fontWeight: 900,
        textTransform: "uppercase",
        letterSpacing: "0.2em",
        color: hovered ? T.accent : T.textSecondary,
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
        gap: 7,
        transition: "color 0.2s",
        position: "relative",
      }}
    >
      {/* gold dot */}
      <span style={{
        width: 4, height: 4, borderRadius: "50%",
        background: hovered ? T.accent : T.textMuted,
        transition: "background 0.2s",
        flexShrink: 0,
      }} />
      {children}
      {/* underline */}
      <motion.span
        animate={{ scaleX: hovered ? 1 : 0, opacity: hovered ? 1 : 0 }}
        transition={{ duration: 0.22 }}
        style={{
          position: "absolute",
          bottom: -4, left: 0, right: 0,
          height: 1,
          background: T.accent,
          transformOrigin: "left",
        }}
      />
    </Link>
  );
};

export default function TopNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { isLoggedIn, logoutUser } = useAuth();
  const {
    publicAnnouncements,
    publicAnnouncementsLoading,
    fetchPublicAnnouncements,
  } = useAppContext();

  const [announcePopupOpen, setAnnouncePopupOpen] = useState(false);
  const [announceSidebarOpen, setAnnounceSidebarOpen] = useState(false);

  /* ── All original logic unchanged ── */
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const run = async () => {
      const list = await fetchPublicAnnouncements?.();
      const latestId =
        Array.isArray(list) && list[0]?._id ? String(list[0]._id) : "";
      if (!latestId) return;
      const seen = getSeen();
      if (!seen || seen.id !== latestId) setAnnouncePopupOpen(true);
    };
    run();
  }, [fetchPublicAnnouncements]);

  const closeAnnouncementPopup = () => {
    const latestId =
      Array.isArray(publicAnnouncements) && publicAnnouncements[0]?._id
        ? String(publicAnnouncements[0]._id)
        : "";
    if (latestId) setSeen(latestId);
    setAnnouncePopupOpen(false);
  };

  const openAnnouncementSidebar = async () => {
    setAnnouncePopupOpen(false);
    setAnnounceSidebarOpen(true);
    await fetchPublicAnnouncements?.();
  };

  const navLinks = [
    { label: "About Us", path: "/about" },
    { label: "Plans", path: "/plans" },
    { label: "Contact Us", path: "/contact" },
  ];

  return (
    <>
      <WithdrawTicker />

      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        style={{
          top: 0,
          left: 0,
          right: 0,
          height: 72,
          zIndex: 100,
          background: scrolled
            ? "rgba(5,13,26,0.95)"
            : "rgba(5,13,26,0.85)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: `1px solid ${scrolled ? "rgba(234,179,8,0.18)" : "rgba(26,45,74,0.8)"}`,
          boxShadow: scrolled
            ? "0 4px 32px rgba(0,0,0,0.5), 0 1px 0 rgba(234,179,8,0.1)"
            : "none",
          transition: "all 0.4s ease",
          overflow: "hidden",
        }}
      >
        {/* 3D animated grid canvas */}
        <NavCanvas />

        {/* Gold top-line accent */}
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 2,
          background: `linear-gradient(90deg, transparent, ${T.accent}, transparent)`,
          opacity: scrolled ? 1 : 0.5,
          transition: "opacity 0.4s",
        }} />

        <div style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "0 24px",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
          zIndex: 1,
        }}>

          {/* Left: Logo + Desktop Links */}
          <div style={{ display: "flex", alignItems: "center", gap: 40 }}>
            <Link to="/" style={{ textDecoration: "none" }}>
              <ThreeWorkersLogo />
            </Link>

            {!isLoggedIn && (
              <div style={{
                display: "none",
                alignItems: "center",
                gap: 28,
              }}
                className="lg-nav-links"
              >
                <style>{`
                  @media (min-width: 1024px) {
                    .lg-nav-links { display: flex !important; }
                  }
                `}</style>
                {navLinks.map((link, i) => (
                  <NavLink key={i} to={link.path}>{link.label}</NavLink>
                ))}
              </div>
            )}
          </div>

          {/* Right: Actions */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {!isLoggedIn ? (
              <button
                onClick={() => navigate("/login")}
                style={{
                  display: "none",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 22px",
                  background: T.accent,
                  color: "#0a0d14",
                  fontWeight: 900,
                  fontSize: 10,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  border: "none",
                  borderRadius: 12,
                  cursor: "pointer",
                  boxShadow: `0 4px 20px ${T.accentGlow}`,
                  transition: "transform 0.18s, box-shadow 0.18s",
                }}
                className="sm-connect-btn"
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 6px 28px rgba(234,179,8,0.55)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "";
                  e.currentTarget.style.boxShadow = `0 4px 20px ${T.accentGlow}`;
                }}
              >
                <style>{`
                  @media (min-width: 640px) { .sm-connect-btn { display: flex !important; } }
                `}</style>
                Connect Terminal
              </button>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <NavIconButton icon={<FaBullhorn />} onClick={openAnnouncementSidebar} />
                <div className="sm-headset-btn" style={{ display: "none" }}>
                  <style>{`@media (min-width: 640px) { .sm-headset-btn { display: block !important; } }`}</style>
                  <NavIconButton icon={<FaHeadset />} onClick={() => navigate("/contact")} />
                </div>
                {/* Logout – red danger */}
                <button
                  onClick={logoutUser}
                  style={{
                    width: 40, height: 40, borderRadius: 12,
                    background: T.dangerDim,
                    border: "1px solid rgba(239,68,68,0.25)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: T.danger,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    fontSize: 14,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = T.danger;
                    e.currentTarget.style.color = "#fff";
                    e.currentTarget.style.borderColor = T.danger;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = T.dangerDim;
                    e.currentTarget.style.color = T.danger;
                    e.currentTarget.style.borderColor = "rgba(239,68,68,0.25)";
                  }}
                >
                  <FaSignOutAlt size={14} />
                </button>
              </div>
            )}

            {/* Burger (mobile/tablet) */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              style={{
                width: 40, height: 40, borderRadius: 12,
                background: "rgba(255,255,255,0.04)",
                border: `1px solid ${T.border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: T.textSecondary, cursor: "pointer",
                transition: "all 0.2s",
              }}
              className="lg-hide-burger"
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = T.accent;
                e.currentTarget.style.color = T.accent;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = T.border;
                e.currentTarget.style.color = T.textSecondary;
              }}
            >
              <style>{`@media (min-width: 1024px) { .lg-hide-burger { display: none !important; } }`}</style>
              <FaBars size={15} />
            </button>
          </div>
        </div>
      </motion.nav>

      {/* ════════════ MOBILE MENU SIDEBAR ════════════ */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              style={{
                position: "fixed", inset: 0, zIndex: 1001,
                background: "rgba(2,6,15,0.75)",
                backdropFilter: "blur(8px)",
              }}
            />

            {/* Panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", bounce: 0, duration: 0.4 }}
              style={{
                position: "fixed", top: 0, right: 0,
                height: "100%", width: 300,
                background: T.bgCard,
                border: `1px solid ${T.border}`,
                borderRight: "none",
                zIndex: 1002,
                display: "flex", flexDirection: "column",
                padding: 24,
                boxShadow: "-12px 0 60px rgba(0,0,0,0.6)",
                overflow: "hidden",
              }}
            >
              {/* Top-line gold accent */}
              <div style={{
                position: "absolute", top: 0, left: 0, right: 0, height: 2,
                background: `linear-gradient(90deg,${T.accent},transparent)`,
              }} />

              {/* Subtle grid bg for panel */}
              <div style={{
                position: "absolute", inset: 0, pointerEvents: "none",
                backgroundImage: `
                  linear-gradient(rgba(234,179,8,0.03) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(59,130,246,0.025) 1px, transparent 1px)
                `,
                backgroundSize: "40px 40px",
              }} />

              {/* Header */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                marginBottom: 36, position: "relative", zIndex: 1,
              }}>
                <ThreeWorkersLogo />
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: "rgba(255,255,255,0.04)",
                    border: `1px solid ${T.border}`,
                    color: T.textSecondary, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = T.accent; e.currentTarget.style.borderColor = T.accent; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = T.textSecondary; e.currentTarget.style.borderColor = T.border; }}
                >
                  <FaTimes size={14} />
                </button>
              </div>

              {/* Nav links */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4, position: "relative", zIndex: 1 }}>
                {navLinks.map((link, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.07, duration: 0.3 }}
                  >
                    <Link
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      style={{
                        display: "flex", alignItems: "center", gap: 12,
                        padding: "14px 16px",
                        borderRadius: 12,
                        textDecoration: "none",
                        color: T.textSecondary,
                        fontSize: 11,
                        fontWeight: 900,
                        textTransform: "uppercase",
                        letterSpacing: "0.18em",
                        borderBottom: `1px solid ${T.border}`,
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = T.accent;
                        e.currentTarget.style.background = T.accentDim;
                        e.currentTarget.style.borderBottomColor = "rgba(234,179,8,0.25)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = T.textSecondary;
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.borderBottomColor = T.border;
                      }}
                    >
                      <div style={{
                        width: 6, height: 6, borderRadius: "50%",
                        background: T.accent, opacity: 0.6, flexShrink: 0,
                      }} />
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
              </div>

              {/* Bottom actions */}
              <div style={{
                marginTop: "auto",
                paddingTop: 24,
                borderTop: `1px solid ${T.border}`,
                display: "flex",
                flexDirection: "column",
                gap: 10,
                position: "relative",
                zIndex: 1,
              }}>
                {!isLoggedIn ? (
                  <button
                    onClick={() => { navigate("/login"); setMobileMenuOpen(false); }}
                    style={{
                      width: "100%",
                      padding: "14px 0",
                      background: T.accent,
                      color: "#0a0d14",
                      fontWeight: 900,
                      fontSize: 11,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      border: "none",
                      borderRadius: 14,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      boxShadow: `0 4px 20px ${T.accentGlow}`,
                    }}
                  >
                    <FaSignInAlt /> Login Terminal
                  </button>
                ) : (
                  <button
                    onClick={() => { logoutUser(); setMobileMenuOpen(false); }}
                    style={{
                      width: "100%", padding: "14px 0",
                      background: T.dangerDim,
                      color: T.danger,
                      fontWeight: 900, fontSize: 11,
                      letterSpacing: "0.18em", textTransform: "uppercase",
                      border: "1px solid rgba(239,68,68,0.25)",
                      borderRadius: 14, cursor: "pointer",
                    }}
                  >
                    Disconnect
                  </button>
                )}
                <button
                  onClick={() => { navigate("/contact"); setMobileMenuOpen(false); }}
                  style={{
                    width: "100%", padding: "13px 0",
                    background: "rgba(255,255,255,0.04)",
                    color: T.textSecondary,
                    fontWeight: 900, fontSize: 11,
                    letterSpacing: "0.18em", textTransform: "uppercase",
                    border: `1px solid ${T.border}`,
                    borderRadius: 14, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  }}
                >
                  <FaHeadset /> Get Support
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>



      {/* ════════════ ANNOUNCEMENT SIDEBAR ════════════ */}
      <AnimatePresence>
        {announceSidebarOpen && (
          <>
            <motion.div
              onClick={() => setAnnounceSidebarOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: "fixed", inset: 0, zIndex: 999,
                background: "rgba(2,6,15,0.7)",
                backdropFilter: "blur(8px)",
              }}
            />
            <motion.aside
              initial={{ x: "110%" }}
              animate={{ x: 0 }}
              exit={{ x: "110%" }}
              transition={{ type: "spring", bounce: 0.05, duration: 0.45 }}
              style={{
                position: "fixed", top: 0, right: 0, zIndex: 1000,
                width: "90%", maxWidth: 400, height: "100%",
                background: T.bgCard,
                borderLeft: `1px solid ${T.border}`,
                boxShadow: "-12px 0 60px rgba(0,0,0,0.5)",
                overflowY: "auto",
                display: "flex", flexDirection: "column",
              }}
            >
              {/* Gold left-edge accent stripe */}
              <div style={{
                position: "absolute", top: 0, left: 0, bottom: 0, width: 2,
                background: `linear-gradient(180deg,${T.accent},transparent)`,
                pointerEvents: "none",
              }} />

              {/* Subtle dot grid bg */}
              <div style={{
                position: "absolute", inset: 0, pointerEvents: "none",
                backgroundImage: `radial-gradient(rgba(234,179,8,0.06) 1px, transparent 1px)`,
                backgroundSize: "28px 28px",
              }} />

              {/* Sticky header */}
              <div style={{
                position: "sticky", top: 0,
                height: 72,
                padding: "0 24px",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                borderBottom: `1px solid ${T.border}`,
                background: "rgba(10,22,40,0.92)",
                backdropFilter: "blur(16px)",
                zIndex: 2,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {/* Gold icon box */}
                  <div style={{
                    width: 40, height: 40, borderRadius: 12,
                    background: T.accentDim,
                    border: `1px solid rgba(234,179,8,0.3)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: T.accent, fontSize: 15,
                  }}>
                    <FaBullhorn />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 900, color: T.textPrimary, letterSpacing: "0.15em", textTransform: "uppercase" }}>
                      Announcements
                    </div>
                    <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>
                      {publicAnnouncements?.length ?? 0} updates
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setAnnounceSidebarOpen(false)}
                  style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: "rgba(255,255,255,0.04)",
                    border: `1px solid ${T.border}`,
                    color: T.textSecondary, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = T.accent; e.currentTarget.style.borderColor = T.accent; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = T.textSecondary; e.currentTarget.style.borderColor = T.border; }}
                >
                  <FaTimes size={13} />
                </button>
              </div>

              {/* Items */}
              <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12, position: "relative", zIndex: 1 }}>
                {publicAnnouncementsLoading ? (
                  [1, 2, 3].map((i) => (
                    <div key={i} style={{
                      height: 90, borderRadius: 16,
                      background: "rgba(255,255,255,0.03)",
                      border: `1px solid ${T.border}`,
                      animation: "pulse 1.5s ease-in-out infinite",
                    }} />
                  ))
                ) : publicAnnouncements?.map((a, idx) => (
                  <motion.div
                    key={a._id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.06 }}
                    style={{
                      borderRadius: 16,
                      background: "rgba(255,255,255,0.02)",
                      border: `1px solid ${T.border}`,
                      padding: "18px 20px",
                      cursor: "default",
                      transition: "all 0.22s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = T.accentDim;
                      e.currentTarget.style.borderColor = "rgba(234,179,8,0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(255,255,255,0.02)";
                      e.currentTarget.style.borderColor = T.border;
                    }}
                  >
                    {/* Gold left tick */}
                    <div style={{
                      display: "flex", alignItems: "flex-start", gap: 12,
                    }}>
                      <div style={{
                        width: 3, height: "100%", minHeight: 40,
                        background: T.accent, borderRadius: 2, flexShrink: 0, marginTop: 2,
                      }} />
                      <div>
                        <h3 style={{
                          fontSize: 12, fontWeight: 900,
                          color: T.textPrimary,
                          textTransform: "uppercase", letterSpacing: "0.1em",
                          marginBottom: 6,
                        }}>
                          {a.title}
                        </h3>
                        <p style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.6, margin: 0 }}>
                          {a.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Original popup modal — logic unchanged */}
      <AnnouncementPopupModal
        open={announcePopupOpen}
        onClose={closeAnnouncementPopup}
        list={publicAnnouncements}
        loading={publicAnnouncementsLoading}
      />
    </>
  );
}